import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import { useJobInfoStyles } from './JobInfoStyles';
import { sanitizeHtml } from 'utils';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { KeyboardDatePicker } from '@material-ui/pickers';
import Typography from '@material-ui/core/Typography';
import InfoIcon from '@material-ui/icons/Info';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { useDataProvider } from 'react-admin';
import OptionsHistoryModal, {
  formatPaintPayoutAmount,
  historyItemToRow,
} from './OptionsHistoryModal';
import { CAN_CHECK_UNCHECK_HOUSE } from 'providers/permissions';
import { PhaseStructureTypeEnum } from '@vatos-pas/common';
import Chip from '@material-ui/core/Chip/Chip';
import { TrimOptionStatus } from './TrimOptionStatus';

interface JobInfoProps {
  theJob: any;
  checkHouse: any;
}

const getJobAddressInfo = (theJob: any) => {
  if (!theJob?.job) return '';
  return `${theJob.job.city || ''} - ${theJob.job.state || ''} - ${
    theJob.job.zip || ''
  }`
    .replace(/^\s*-\s*|\s*-\s*$/g, '')
    .replace(/\s*-\s*-\s*/g, ' - ');
};

export const JobInfo: React.FC<JobInfoProps> = ({ theJob, checkHouse }) => {
  const classes = useJobInfoStyles();
  const { hasPermission } = PermissionsProvider.useContainer();
  const dataProvider = useDataProvider();

  // Modal states for options history
  const [openInteriorHistory, setOpenInteriorHistory] = useState(false);
  const [openExteriorHistory, setOpenExteriorHistory] = useState(false);
  const [interiorHistoryData, setInteriorHistoryData] = useState<any[]>([]);
  const [exteriorHistoryData, setExteriorHistoryData] = useState<any[]>([]);
  const [loadingInterior, setLoadingInterior] = useState(false);
  const [loadingExterior, setLoadingExterior] = useState(false);

  // Load history data when modal opens
  const loadInteriorHistory = async () => {
    if (!theJob?.id) return;

    setLoadingInterior(true);
    try {
      const result = await dataProvider.getList(
        'job-interior-exterior-options-history-report',
        {
          filter: { jobId: theJob.id },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'id', order: 'DESC' },
        },
      );

      // Filter only interior options
      const interiorData = (result.data || []).filter(
        (item: any) => item.typeOption === 'Interior',
      );
      setInteriorHistoryData(interiorData);
    } catch (error) {
      console.error('Error loading interior history:', error);
      setInteriorHistoryData([]);
    } finally {
      setLoadingInterior(false);
    }
  };

  const loadExteriorHistory = async () => {
    if (!theJob?.id) return;

    setLoadingExterior(true);
    try {
      const result = await dataProvider.getList(
        'job-interior-exterior-options-history-report',
        {
          filter: { jobId: theJob.id },
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'id', order: 'DESC' },
        },
      );

      // Filter only exterior options
      const exteriorData = (result.data || []).filter(
        (item: any) => item.typeOption === 'Exterior',
      );
      setExteriorHistoryData(exteriorData);
    } catch (error) {
      console.error('Error loading exterior history:', error);
      setExteriorHistoryData([]);
    } finally {
      setLoadingExterior(false);
    }
  };

  const handleInteriorIconClick = () => {
    setOpenInteriorHistory(true);
    loadInteriorHistory();
  };

  const handleExteriorIconClick = () => {
    setOpenExteriorHistory(true);
    loadExteriorHistory();
  };
  const inputProps = {
    InputLabelProps: { shrink: true, className: classes.black },
    InputProps: { className: classes.black },
  };

  const getPhaseName = () => {
    if (theJob.job?.currentJobPhasePriority === 100) {
      return 'Materials';
    }
    if (theJob.job?.currentJobPhasePriority === 200) {
      return 'Garage';
    }
    return theJob.jobPhase?.phaseName || '';
  };

  return (
    <Box className={classes.whiteBox}>
      <Box m={3}>
        <Box display="flex" alignItems="center">
          <TextField
            label="Project Phase"
            fullWidth
            disabled
            className={classes.input}
            value={getPhaseName()}
            {...inputProps}
          />
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="MM/dd/yyyy"
            margin="normal"
            id="date-picker-inline"
            label="Project Approval - Due Date"
            fullWidth
            disabled
            className={classes.inputNoMarginRight}
            value={theJob.job?.dateDue || null}
            onChange={(date: MaterialUiPickersDate) => date}
            KeyboardButtonProps={{
              'aria-label': 'change date',
            }}
            {...inputProps}
          />
        </Box>
        <Box display="flex" alignItems="center">
          <TextField
            label="Builder"
            fullWidth
            {...inputProps}
            disabled
            className={classes.input}
            value={theJob.job?.builderName || ''}
          />
          <TextField
            label="Job ID #"
            fullWidth
            {...inputProps}
            disabled
            value={theJob.job?.idNumber || ''}
            className={classes.inputNoMarginRight}
          />
        </Box>
        <TextField
          label="Current Supplier"
          fullWidth
          {...inputProps}
          disabled
          className={classes.input}
          value={theJob.job?.currentSupplierName || ''}
        />
        <Box display="flex" alignItems="center" mt={1}>
          <TextField
            label="Subdivision"
            fullWidth
            {...inputProps}
            disabled
            className={classes.input}
            value={theJob.job?.subdivisionName || ''}
          />
          <Box
            width="100%"
            ml={2}
            style={{ borderBottom: '1px dotted gray', marginTop: '-14px' }}
          >
            <Box>
              <Typography style={{ fontSize: 12 }}>Supervisors</Typography>
            </Box>
            <Box display="flex" flexWrap="wrap">
              {theJob.job?.supervisorFullNames &&
                theJob.job.supervisorFullNames
                  .split(',')
                  .map(item => (
                    <Chip
                      style={{ margin: '5px 5px' }}
                      key={item}
                      label={item}
                    />
                  ))}
            </Box>
          </Box>
        </Box>
        <Box display="flex" alignItems="center">
          <Box width="100%">
            <TextField
              label="Model"
              fullWidth
              {...inputProps}
              disabled
              className={classes.input}
              value={theJob.job?.modelName || ''}
            />
          </Box>
          <Box width="100%" display="flex" pl={2}>
            <TextField
              label="Building #"
              fullWidth
              {...inputProps}
              disabled
              className={classes.input}
              value={theJob.job?.buildingNumber || ''}
            />
            <TextField
              label="Lot"
              fullWidth
              {...inputProps}
              disabled
              value={theJob.job?.lot || ''}
              className={classes.inputNoMarginRight}
            />
            <TextField
              label="Block"
              fullWidth
              {...inputProps}
              disabled
              value={theJob.job?.block || ''}
              className={classes.inputNoMarginRight}
            />
          </Box>
        </Box>
        <Box display="flex" alignItems="center">
          <TextField
            label="Address"
            fullWidth
            {...inputProps}
            disabled
            value={theJob.job?.address1 || ''}
            className={classes.input}
          />
          <TextField
            label="City, State, Zip"
            fullWidth
            {...inputProps}
            disabled
            value={getJobAddressInfo(theJob)}
            className={classes.inputNoMarginRight}
          />
        </Box>
        <Box display="flex" width="100%" flexDirection="row">
          <Box width="50%" display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={theJob.job?.houseMarked || false}
                  onChange={(event: any) => checkHouse(event.target.checked)}
                  name="isMonday"
                  disabled={
                    !hasPermission(CAN_CHECK_UNCHECK_HOUSE) ||
                    theJob.job?.houseMarked
                  }
                  color="primary"
                />
              }
              label="Mark House"
            />
          </Box>
          <Box width="50%">
            {theJob.job?.dateHouseMarked && (
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                id="date-picker-inline"
                label="House marked on"
                fullWidth
                onChange={() => {
                  // Date house marked is read-only, managed by checkHouse function
                }}
                value={
                  theJob.job?.dateHouseMarked
                    ? new Date(theJob.job.dateHouseMarked)
                    : null
                }
                disabled
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            )}
          </Box>
        </Box>
        <Box borderTop="1px solid #e0e0e0" pt={2}></Box>
        <Box display="flex">
          <Box display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={theJob.job?.garageFirst || false}
                  name="garageFirst"
                  disabled
                  color="primary"
                />
              }
              label="Garage"
            />
          </Box>
          {theJob?.job?.phaseStructureType ===
            PhaseStructureTypeEnum.DrywallAndPaint && (
            <Box display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={theJob.job?.hasTrim || false}
                    name="hasTrim"
                    disabled
                    color="primary"
                  />
                }
                label="Trim"
              />
            </Box>
          )}
          {theJob?.job?.phaseStructureType ===
            PhaseStructureTypeEnum.DrywallAndPaint && (
            <Box display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={theJob.job?.hasExteriorPaint || false}
                    name="hasExteriorPaint"
                    disabled
                    color="primary"
                  />
                }
                label="Exterior Paint"
              />
            </Box>
          )}
        </Box>
        <Box display="flex" flexWrap="wrap">
          {theJob.job?.hasInteriorOptions && (
            <Box display="flex" alignItems="center" mr={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={theJob.job?.interiorOptionsFinished || false}
                    name="interiorOptionsFinished"
                    disabled
                    color="primary"
                  />
                }
                label="Paint Interior Options"
              />
              <InfoIcon
                style={{
                  marginLeft: '2px',
                  cursor: 'pointer',
                  color: '#1976d2',
                  fontSize: '20px',
                }}
                onClick={handleInteriorIconClick}
              />
            </Box>
          )}
          {theJob.job?.hasExteriorOptions && (
            <Box display="flex" alignItems="center" mr={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={theJob.job?.exteriorOptionsFinished || false}
                    name="exteriorOptionsFinished"
                    disabled
                    color="primary"
                  />
                }
                label="Paint Exterior Options"
              />
              <InfoIcon
                style={{
                  marginLeft: '2px',
                  cursor: 'pointer',
                  color: '#1976d2',
                  fontSize: '20px',
                }}
                onClick={handleExteriorIconClick}
              />
            </Box>
          )}
          <TrimOptionStatus
            jobId={theJob.id}
            hasTrimOptions={theJob.job?.hasTrimOptions}
            trimOptionsFinished={theJob.job?.trimOptionsFinished}
            trimOptionsFinishedJobPhaseId={
              theJob.job?.trimOptionsFinishedJobPhaseId
            }
            jobPhases={theJob.jobPhases}
          />
          <Box display="flex" alignItems="center" mr={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={theJob.job?.highCeiling || false}
                  name="highCeiling"
                  disabled
                  color="primary"
                />
              }
              label="High Ceiling"
            />
          </Box>
          <Box display="flex" alignItems="center">
            <FormControlLabel
              control={
                <Checkbox
                  checked={theJob.job?.screwInspection || false}
                  name="screwInspection"
                  disabled
                  color="primary"
                />
              }
              label="Screw Inspection"
            />
          </Box>
        </Box>
        <Box mt={2}>
          <Box className={classes.titleTab}>
            <Typography className={classes.titleFont}>Options</Typography>
          </Box>
          {theJob.job?.options && (
            <div
              className="preview"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(theJob.job.options),
              }}
            ></div>
          )}
        </Box>
      </Box>

      {/* Options History Modals */}
      <OptionsHistoryModal
        open={openInteriorHistory}
        onClose={() => setOpenInteriorHistory(false)}
        title="Paint Interior Options"
        rows={interiorHistoryData.map(historyItemToRow)}
        loading={loadingInterior}
        payoutAmount={formatPaintPayoutAmount(
          theJob.job?.interiorCalculatedCost,
        )}
      />

      <OptionsHistoryModal
        open={openExteriorHistory}
        onClose={() => setOpenExteriorHistory(false)}
        title="Paint Exterior Options"
        rows={exteriorHistoryData.map(historyItemToRow)}
        loading={loadingExterior}
        payoutAmount={formatPaintPayoutAmount(
          theJob.job?.exteriorCalculatedCost,
        )}
      />
    </Box>
  );
};

export default JobInfo;
